import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAnnotations,
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_CONNECTION_CHART,
    EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_NETWORK_PERF_CHART,
    EXPENSES_INVESTMENTS_CAPEX_DEALER_AMOUNTS_CHART,
    EXPENSES_INVESTMENTS_CAPEX_KPIS,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_TYPE_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_WORK_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_TYPE_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_WORK_CHART,
} from '../../assets/keys';

const KEYS = [
    EXPENSES_INVESTMENTS_CAPEX_KPIS,
    EXPENSES_INVESTMENTS_CAPEX_DEALER_AMOUNTS_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_TYPE_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_WORK_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_TYPE_CHART,
    EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_WORK_CHART,
    EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_NETWORK_PERF_CHART,
    EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_CONNECTION_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    totalInvestments?: number;
    totalInvestmentsEvolution?: number;

    includingPsInvestments?: number;
    includingPsInvestmentsEvolution?: number;

    connectionInvestments?: number;
    connectionInvestmentsEvolution?: number;

    networkPerformanceInvestments?: number;
    networkPerformanceInvestmentsEvolution?: number;
}
interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=930
    total?: number;
    //kpi_id=931
    connection?: number;
    //kpi_id=932
    networkPerformance?: number;
    //kpi_id=933
    securityEnvironment?: number;
    //kpi_id=934
    logistics?: number;
    //kpi_id=953
    includingSourceStations?: number;
    //kpi_id=956
    investmentsLinkySmartGrid?: number;
    error?: boolean;
}

interface LgtMvTypeChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=944
    total?: number;
    //kpi_id=945
    underground?: number;
    //kpi_id=946
    twisted?: number;
    //kpi_id=947
    overheadBare?: number;
    error?: boolean;
}

interface LgtMvWorkChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=944
    total?: number;
    //kpi_id=945
    extension?: number;
    //kpi_id=946
    renewal?: number;
    //kpi_id=947
    reinforcement?: number;
    error?: boolean;
}

interface LgtTypeChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=944
    total?: number;
    //kpi_id=945
    underground?: number;
    //kpi_id=946
    twisted?: number;
    //kpi_id=947
    overheadBare?: number;
    error?: boolean;
}

interface LgtWorkChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=944
    total?: number;
    //kpi_id=945
    extension?: number;
    //kpi_id=946
    renewal?: number;
    //kpi_id=947
    reinforcement?: number;
    error?: boolean;
}

interface NtPerfChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=944
    total?: number;
    //kpi_id=945
    reinforcementLv?: number;
    //kpi_id=946
    reinforcementMv?: number;
    //kpi_id=947
    actionsPac?: number;
    //kpi_id=947
    actionsExcluPdvRp?: number;
    //kpi_id=947
    actionsPdvRp?: number;
    //kpi_id=947
    operatingResources?: number;
    error?: boolean;
}

interface ConnectionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=974
    total?: number;
    //kpi_id=971
    consumersLv?: number;
    //kpi_id=970
    consumersMv?: number;
    //kpi_id=973
    producersLv?: number;
    //kpi_id=972
    producersMv?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-expenses-investments',
    templateUrl: './expenses-investments.component.html',
    styleUrls: ['./expenses-investments.component.scss'],
})
export class ExpensesInvestmentsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.investments.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_KPIS),
    ) as Signal<KpiData[]>;

    public dashArray = [0, 0, 0, 0, 0, 0, 8];

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_DEALER_AMOUNTS_CHART),
    ) as Signal<ChartData[]>;
    public chartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: 1,
            1: 1,
            2: 1,
            3: 1,
            4: 1,
            5: 1,
            6: 1,
        }),
        style: this.setDataLabelsStyleColors(this.texts.dealerInvestmentAmountsChart.totalTitle),
    };
    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.dealerInvestmentAmountsChart.connectionAxisTitle,
                this.texts.dealerInvestmentAmountsChart.logisticsAxisTitle,
                this.texts.dealerInvestmentAmountsChart.networkPerformanceAxisTitle,
                this.texts.dealerInvestmentAmountsChart.investmentsLinkySmartGridAxisTitle,
                this.texts.dealerInvestmentAmountsChart.securityEnvironmentAxisTitle,
                this.texts.dealerInvestmentAmountsChart.includingSourceStationsAxisTitle,
            ],
            min: 0,
            title: {
                text: this.texts.dealerInvestmentAmountsChart.yaxisTitleLeft,
            },
        },
    ];
    public chartError: boolean = true;
    public investmentLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec3Green,
                colorAec4Orange,
                colorAec1BlueLight,
                colorAec6Gray,
                colorAec7RedLight,
                colorAec2BlueDark,
                colorBlack,
            ],
        },
    };
    public dealerInvestmentAmountsCustomTooltip: ApexTooltip['custom'];

    public lgtMvTypeChartData: Signal<LgtMvTypeChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_TYPE_CHART),
    ) as Signal<LgtMvTypeChartData[]>;
    public lgtMvTypeChartSeries: ApexAxisChartSeries = [];
    public lgtMvTypeDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
        style: this.setDataLabelsStyleColors(this.texts.lengthMvServiceStructureChart.totalTitle),
    };
    public lgtMvTypeXaxisCategories: ApexXAxis['categories'];
    public lgtMvTypeYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.lengthMvServiceStructureChart.undergroundAxisTitle,
                this.texts.lengthMvServiceStructureChart.twistedAxisTitle,
                this.texts.lengthMvServiceStructureChart.overheadBareAxisTitle,
            ],
            title: {
                text: this.texts.lengthMvServiceStructureChart.yaxisTitleLeft,
            },
        },
    ];
    public lgtMvTypeChartError: boolean = true;
    public lgtMvTypeLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec2BlueDark, colorAec3Green, colorAec5Brown, colorBlack],
        },
    };
    public lengthMvServiceStructureCustomTooltip: ApexTooltip['custom'];

    public lgtMvWorkChartData: Signal<LgtMvWorkChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_LGHT_MV_WORK_CHART),
    ) as Signal<LgtMvWorkChartData[]>;
    public lgtMvWorkChartSeries: ApexAxisChartSeries = [];
    public lgtMvWorkXaxisCategories: ApexXAxis['categories'];
    public lgtMvWorkYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.lengthMvServiceWorkChart.extensionAxisTitle,
                this.texts.lengthMvServiceWorkChart.renewalAxisTitle,
                this.texts.lengthMvServiceWorkChart.reinforcementAxisTitle,
            ],
            title: {
                text: this.texts.lengthMvServiceWorkChart.yaxisTitleLeft,
            },
        },
    ];
    public lgtMvWorkDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3, 4]),
        style: this.setDataLabelsStyleColors(this.texts.lengthMvServiceWorkChart.totalTitle),
    };
    public lgtMvWorkChartError: boolean = true;
    public lgtMvWorkLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec1BlueLight, colorAec6Gray, colorAec4Orange, colorBlack],
        },
    };
    public lengthMvServiceWorkCustomTooltip: ApexTooltip['custom'];

    public lgtLvTypeChartData: Signal<LgtTypeChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_TYPE_CHART),
    ) as Signal<LgtTypeChartData[]>;
    public lgtLvTypeChartSeries: ApexAxisChartSeries = [];
    public lgtLvTypeDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3, 4]),
        style: this.setDataLabelsStyleColors(this.texts.lengthLvServiceStructureChart.totalTitle),
    };
    public lgtLvTypeXaxisCategories: ApexXAxis['categories'];
    public lgtLvTypeYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.lengthLvServiceStructureChart.undergroundAxisTitle,
                this.texts.lengthLvServiceStructureChart.twistedAxisTitle,
                this.texts.lengthLvServiceStructureChart.overheadBareAxisTitle,
            ],
            title: {
                text: this.texts.lengthLvServiceStructureChart.yaxisTitleLeft,
            },
        },
    ];
    public lgtLvTypeChartError: boolean = true;
    public lgtLvTypeLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec2BlueDark, colorAec3Green, colorAec5Brown, colorBlack],
        },
    };
    public lgtLvTypeCustomTooltip: ApexTooltip['custom'];

    public lgtLvWorkChartData: Signal<LgtWorkChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_LGHT_LV_WORK_CHART),
    ) as Signal<LgtWorkChartData[]>;
    public lgtLvWorkChartSeries: ApexAxisChartSeries = [];
    public lgtLvWorkDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3]),
        style: this.setDataLabelsStyleColors(this.texts.lengthLvServiceWorkChart.totalTitle),
    };
    public lgtLvWorkXaxisCategories: ApexXAxis['categories'];
    public lgtLvWorkYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.lengthLvServiceWorkChart.extensionAxisTitle,
                this.texts.lengthLvServiceWorkChart.renewalAxisTitle,
                this.texts.lengthLvServiceWorkChart.reinforcementAxisTitle,
            ],
            title: {
                text: this.texts.lengthLvServiceWorkChart.yaxisTitleLeft,
            },
        },
    ];
    public lgtWorkYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.lengthMvServiceWorkChart.extensionAxisTitle,
        },
        {
            seriesName: this.texts.lengthMvServiceWorkChart.extensionAxisTitle,
        },
        {
            seriesName: this.texts.lengthMvServiceWorkChart.extensionAxisTitle,
        },
        {
            seriesName: this.texts.lengthMvServiceWorkChart.totalAxisTitle,
            min: 0,
            opposite: true,
        },
    ];
    public lgtLvWorkChartError: boolean = true;
    public lgtLvWorkLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec1BlueLight, colorAec6Gray, colorAec4Orange, colorBlack],
        },
    };
    public lengthLvServiceWorkTooltip: ApexTooltip['custom'];

    public amtNetworkPerfChartData: Signal<NtPerfChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_NETWORK_PERF_CHART),
    ) as Signal<NtPerfChartData[]>;
    public amtNetworkPerfChartSeries: ApexAxisChartSeries = [];
    public amtNetworkPerfDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3, 4, 5, 6]),
        style: this.setDataLabelsStyleColors(this.texts.amountsNetworkPerfChart.totalTitle),
    };
    public amtNetworkPerfXaxisCategories: ApexXAxis['categories'];
    public amtNetworkPerfYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.amountsNetworkPerfChart.reinforcementLvAxisTitle,
            min: 0,
            title: {
                text: this.texts.amountsNetworkPerfChart.yaxisTitleLeft,
            },
        },
    ];
    public amtNetworkPerfChartError: boolean = true;
    public amtNetworkPerfLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec2BlueDark,
                colorAec3Green,
                colorAec4Orange,
                colorAec5Brown,
                colorAec6Gray,
                colorBlack,
            ],
        },
    };
    public amountsNetworkPerfCustomTooltip: ApexTooltip['custom'];

    public amtConnectionChartData: Signal<ConnectionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_INVESTMENTS_CAPEX_AMOUNTS_CONNECTION_CHART),
    ) as Signal<ConnectionChartData[]>;
    public amtConnectionChartSeries: ApexAxisChartSeries = [];
    public amtConnectionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3, 4]),
        style: this.setDataLabelsStyleColors(this.texts.amountsConnectionChart.totalTitle),
    };
    public amtConnectionXaxisCategories: ApexXAxis['categories'];
    public amtConnectionYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.amountsConnectionChart.consumersLvAxisTitle,
            min: 0,
            title: {
                text: this.texts.amountsConnectionChart.yaxisTitleLeft,
            },
        },
    ];
    public amtConnectionChartError: boolean = true;
    public amtConnectionLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec2BlueDark,
                colorAec4Orange,
                colorAec5Brown,
                colorBlack,
            ],
        },
    };
    public amountsConnectionCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            this.chartError = this.checkForError(dataList, [
                'xYear',
                'connection',
                'logistics',
                'networkPerformance',
                'investmentsLinkySmartGrid',
                'securityEnvironment',
                'includingSourceStations',
            ]);
            if (!this.chartError) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                const totales = this.getTotales(dataList, [
                    'connection',
                    'logistics',
                    'networkPerformance',
                    'investmentsLinkySmartGrid',
                    'securityEnvironment',
                    'includingSourceStations',
                ]);
                this.chartSeries = [
                    {
                        name: this.texts.dealerInvestmentAmountsChart.connectionAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.connection!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart.logisticsAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.logistics!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart.networkPerformanceAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.networkPerformance!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart
                            .investmentsLinkySmartGridAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.investmentsLinkySmartGrid!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart.securityEnvironmentAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.securityEnvironment!),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart
                            .includingSourceStationsAxisTitle,
                        type: 'line',
                        data: dataList.map((data) => data.includingSourceStations!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.dealerInvestmentAmountsChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.dataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6, 7];
            }

            const lgtMvTypeDataList = this.lgtMvTypeChartData();
            this.lgtMvTypeChartError = this.checkForError(lgtMvTypeDataList, [
                'xYear',
                'underground',
                'twisted',
                'overheadBare',
            ]);
            if (!this.lgtMvTypeChartError) {
                this.lgtMvTypeXaxisCategories = lgtMvTypeDataList.map((data) => data.xYear);
                const totales = this.getTotales(lgtMvTypeDataList, [
                    'underground',
                    'twisted',
                    'overheadBare',
                ]);
                this.lgtMvTypeChartSeries = [
                    {
                        name: this.texts.lengthMvServiceStructureChart.undergroundAxisTitle,
                        type: 'column',
                        data: lgtMvTypeDataList.map((data) => data.underground!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.lengthMvServiceStructureChart.twistedAxisTitle,
                        type: 'column',
                        data: lgtMvTypeDataList.map((data) => data.twisted!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lengthMvServiceStructureChart.overheadBareAxisTitle,
                        type: 'column',
                        data: lgtMvTypeDataList.map((data) => data.overheadBare!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.lengthMvServiceStructureChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.lgtMvTypeDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const lgtMvWorkDataList = this.lgtMvWorkChartData();
            this.lgtMvWorkChartError = this.checkForError(lgtMvWorkDataList, [
                'xYear',
                'extension',
                'renewal',
                'reinforcement',
            ]);
            if (!this.lgtMvWorkChartError) {
                this.lgtMvWorkXaxisCategories = lgtMvWorkDataList.map((data) => data.xYear);
                const totales = this.getTotales(lgtMvWorkDataList, [
                    'extension',
                    'renewal',
                    'reinforcement',
                ]);
                this.lgtMvWorkChartSeries = [
                    {
                        name: this.texts.lengthMvServiceWorkChart.extensionAxisTitle,
                        type: 'column',
                        data: lgtMvWorkDataList.map((data) => data.extension!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lengthMvServiceWorkChart.renewalAxisTitle,
                        type: 'column',
                        data: lgtMvWorkDataList.map((data) => data.renewal!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.lengthMvServiceWorkChart.reinforcementAxisTitle,
                        type: 'column',
                        data: lgtMvWorkDataList.map((data) => data.reinforcement!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.lengthMvServiceWorkChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.lgtMvWorkDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const lgtLvTypeDataList = this.lgtLvTypeChartData();
            this.lgtLvTypeChartError = this.checkForError(lgtLvTypeDataList, [
                'xYear',
                'underground',
                'twisted',
                'overheadBare',
            ]);
            if (!this.lgtLvTypeChartError) {
                this.lgtLvTypeXaxisCategories = lgtLvTypeDataList.map((data) => data.xYear);
                const totales = this.getTotales(lgtLvTypeDataList, [
                    'underground',
                    'twisted',
                    'overheadBare',
                ]);
                this.lgtLvTypeChartSeries = [
                    {
                        name: this.texts.lengthLvServiceStructureChart.undergroundAxisTitle,
                        type: 'column',
                        data: lgtLvTypeDataList.map((data) => data.underground!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.lengthLvServiceStructureChart.twistedAxisTitle,
                        type: 'column',
                        data: lgtLvTypeDataList.map((data) => data.twisted!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lengthLvServiceStructureChart.overheadBareAxisTitle,
                        type: 'column',
                        data: lgtLvTypeDataList.map((data) => data.overheadBare!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.lengthMvServiceWorkChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.lgtLvTypeDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const lgtLvWorkDataList = this.lgtLvWorkChartData();
            this.lgtLvWorkChartError = this.checkForError(lgtLvWorkDataList, [
                'xYear',
                'extension',
                'renewal',
                'reinforcement',
            ]);
            if (!this.lgtLvWorkChartError) {
                this.lgtLvWorkXaxisCategories = lgtLvWorkDataList.map((data) => data.xYear);
                const totales = this.getTotales(lgtLvWorkDataList, [
                    'extension',
                    'renewal',
                    'reinforcement',
                ]);
                this.lgtLvWorkChartSeries = [
                    {
                        name: this.texts.lengthLvServiceWorkChart.extensionAxisTitle,
                        type: 'column',
                        data: lgtLvWorkDataList.map((data) => data.extension!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lengthLvServiceWorkChart.renewalAxisTitle,
                        type: 'column',
                        data: lgtLvWorkDataList.map((data) => data.renewal!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.lengthLvServiceWorkChart.reinforcementAxisTitle,
                        type: 'column',
                        data: lgtLvWorkDataList.map((data) => data.reinforcement!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.lengthLvServiceWorkChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.lgtLvWorkDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const amtNetworkPerfDataList = this.amtNetworkPerfChartData();
            this.amtNetworkPerfChartError = this.checkForError(amtNetworkPerfDataList, [
                'xYear',
                'reinforcementLv',
                'reinforcementMv',
                'actionsPac',
                'actionsExcluPdvRp',
                'actionsPdvRp',
                'operatingResources',
            ]);
            if (!this.amtNetworkPerfChartError) {
                this.amtNetworkPerfXaxisCategories = amtNetworkPerfDataList.map(
                    (data) => data.xYear,
                );
                const totales = this.getTotales(amtNetworkPerfDataList, [
                    'reinforcementLv',
                    'reinforcementMv',
                    'actionsPac',
                    'actionsExcluPdvRp',
                    'actionsPdvRp',
                    'operatingResources',
                ]);
                this.amtNetworkPerfChartSeries = [
                    {
                        name: this.texts.amountsNetworkPerfChart.reinforcementLvAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.reinforcementLv!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.reinforcementMvAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.reinforcementMv!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.actionsPacAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.actionsPac!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.actionsExcluPdvRpAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.actionsExcluPdvRp!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.actionsPdvRpAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.actionsPdvRp!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.operatingResourcesAxisTitle,
                        type: 'column',
                        data: amtNetworkPerfDataList.map((data) => data.operatingResources!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.amountsNetworkPerfChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.amtNetworkPerfDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6];
            }

            const amtConnectionDataList = this.amtConnectionChartData();
            this.amtConnectionChartError = this.checkForError(amtConnectionDataList, [
                'xYear',
                'consumersLv',
                'consumersMv',
                'producersLv',
                'producersMv',
            ]);
            if (!this.amtConnectionChartError) {
                this.amtConnectionXaxisCategories = amtConnectionDataList.map((data) => data.xYear);
                const totales = this.getTotales(amtConnectionDataList, [
                    'consumersLv',
                    'consumersMv',
                    'producersLv',
                    'producersMv',
                ]);
                this.amtConnectionChartSeries = [
                    {
                        name: this.texts.amountsConnectionChart.consumersLvAxisTitle,
                        type: 'column',
                        data: amtConnectionDataList.map((data) => data.consumersLv!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.amountsConnectionChart.consumersMvAxisTitle,
                        type: 'column',
                        data: amtConnectionDataList.map((data) => data.consumersMv!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.amountsConnectionChart.producersLvAxisTitle,
                        type: 'column',
                        data: amtConnectionDataList.map((data) => data.producersLv!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.amountsConnectionChart.producersMvAxisTitle,
                        type: 'column',
                        data: amtConnectionDataList.map((data) => data.producersMv!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.amountsConnectionChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.amtConnectionDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.dealerInvestmentAmountsCustomTooltip = this.setDealerInvestmentAmountsCustomTooltip();
        this.lengthMvServiceStructureCustomTooltip =
            this.setLengthMvServiceStructureCustomTooltip();
        this.lengthMvServiceWorkCustomTooltip = this.setLengthMvServiceWorkCustomTooltip();
        this.lgtLvTypeCustomTooltip = this.setLgtLvTypeCustomTooltip();
        this.lengthLvServiceWorkTooltip = this.setLengthLvServiceWorkTooltip();
        this.amountsNetworkPerfCustomTooltip = this.setAmountsNetworkPerfCustomTooltip();
        this.amountsConnectionCustomTooltip = this.setAmountsConnectionCustomTooltip();
    }

    private setDealerInvestmentAmountsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.dealerInvestmentAmountsChart.precision;

            const connectionSerie = w.globals.initialSeries[0];
            const {
                name: connectionName,
                color: connectionColor,
                valueStringified: connectionStringified,
            } = this.setCustomTooltipData(precision, connectionSerie, dataPointIndex);

            const logisticsSerie = w.globals.initialSeries[1];
            const {
                name: logisticsName,
                color: logisticsColor,
                valueStringified: logisticsStringified,
            } = this.setCustomTooltipData(precision, logisticsSerie, dataPointIndex);

            const networkPerformanceSerie = w.globals.initialSeries[2];
            const {
                name: networkPerformanceName,
                color: networkPerformanceColor,
                valueStringified: networkPerformanceStringified,
            } = this.setCustomTooltipData(precision, networkPerformanceSerie, dataPointIndex);

            const investmentsLinkySmartGridSerie = w.globals.initialSeries[3];
            const {
                name: investmentsLinkySmartGridName,
                color: investmentsLinkySmartGridColor,
                valueStringified: investmentsLinkySmartGridStringified,
            } = this.setCustomTooltipData(
                precision,
                investmentsLinkySmartGridSerie,
                dataPointIndex,
            );

            const securityEnvironmentSerie = w.globals.initialSeries[4];
            const {
                name: securityEnvironmentName,
                color: securityEnvironmentColor,
                valueStringified: securityEnvironmentStringified,
            } = this.setCustomTooltipData(precision, securityEnvironmentSerie, dataPointIndex);

            const includingSourceStationsSerie = w.globals.initialSeries[5];
            const {
                name: includingSourceStationsName,
                color: includingSourceStationsColor,
                valueStringified: includingSourceStationsStringified,
            } = this.setCustomTooltipData(precision, includingSourceStationsSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[6];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: connectionColor,
                    name: connectionName,
                    value: connectionStringified,
                },
                {
                    color: logisticsColor,
                    name: logisticsName,
                    value: logisticsStringified,
                },
                {
                    color: networkPerformanceColor,
                    name: networkPerformanceName,
                    value: networkPerformanceStringified,
                },
                {
                    color: investmentsLinkySmartGridColor,
                    name: investmentsLinkySmartGridName,
                    value: investmentsLinkySmartGridStringified,
                },
                {
                    color: securityEnvironmentColor,
                    name: securityEnvironmentName,
                    value: securityEnvironmentStringified,
                },
                {
                    color: includingSourceStationsColor,
                    name: includingSourceStationsName,
                    value: includingSourceStationsStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setLengthMvServiceStructureCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.lengthMvServiceStructureChart.precision;

            const undergroundSerie = w.globals.initialSeries[0];
            const {
                name: undergroundName,
                color: undergroundColor,
                valueStringified: undergroundStringified,
            } = this.setCustomTooltipData(precision, undergroundSerie, dataPointIndex);

            const twistedSerie = w.globals.initialSeries[1];
            const {
                name: twistedName,
                color: twistedColor,
                valueStringified: twistedStringified,
            } = this.setCustomTooltipData(precision, twistedSerie, dataPointIndex);

            const overheadBareSerie = w.globals.initialSeries[2];
            const {
                name: overheadBareName,
                color: overheadBareColor,
                valueStringified: overheadBareStringified,
            } = this.setCustomTooltipData(precision, overheadBareSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: undergroundColor,
                    name: undergroundName,
                    value: undergroundStringified,
                },
                {
                    color: twistedColor,
                    name: twistedName,
                    value: twistedStringified,
                },
                {
                    color: overheadBareColor,
                    name: overheadBareName,
                    value: overheadBareStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setLengthMvServiceWorkCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.lengthMvServiceWorkChart.precision;

            const extensionSerie = w.globals.initialSeries[0];
            const {
                name: extensionName,
                color: extensionColor,
                valueStringified: extensionStringified,
            } = this.setCustomTooltipData(precision, extensionSerie, dataPointIndex);

            const renewalSerie = w.globals.initialSeries[1];
            const {
                name: renewalName,
                color: renewalColor,
                valueStringified: renewalStringified,
            } = this.setCustomTooltipData(precision, renewalSerie, dataPointIndex);

            const reinforcementSerie = w.globals.initialSeries[2];
            const {
                name: reinforcementName,
                color: reinforcementColor,
                valueStringified: reinforcementStringified,
            } = this.setCustomTooltipData(precision, reinforcementSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: extensionColor,
                    name: extensionName,
                    value: extensionStringified,
                },
                {
                    color: renewalColor,
                    name: renewalName,
                    value: renewalStringified,
                },
                {
                    color: reinforcementColor,
                    name: reinforcementName,
                    value: reinforcementStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setLgtLvTypeCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.lengthLvServiceStructureChart.precision;

            const undergroundSerie = w.globals.initialSeries[0];
            const {
                name: undergroundName,
                color: undergroundColor,
                valueStringified: undergroundStringified,
            } = this.setCustomTooltipData(precision, undergroundSerie, dataPointIndex);

            const twistedSerie = w.globals.initialSeries[1];
            const {
                name: twistedName,
                color: twistedColor,
                valueStringified: twistedStringified,
            } = this.setCustomTooltipData(precision, twistedSerie, dataPointIndex);

            const overheadBareSerie = w.globals.initialSeries[2];
            const {
                name: overheadBareName,
                color: overheadBareColor,
                valueStringified: overheadBareStringified,
            } = this.setCustomTooltipData(precision, overheadBareSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: undergroundColor,
                    name: undergroundName,
                    value: undergroundStringified,
                },
                {
                    color: twistedColor,
                    name: twistedName,
                    value: twistedStringified,
                },
                {
                    color: overheadBareColor,
                    name: overheadBareName,
                    value: overheadBareStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setLengthLvServiceWorkTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.lengthLvServiceWorkChart.precision;

            const extensionSerie = w.globals.initialSeries[0];
            const {
                name: extensionName,
                color: extensionColor,
                valueStringified: extensionStringified,
            } = this.setCustomTooltipData(precision, extensionSerie, dataPointIndex);

            const renewalSerie = w.globals.initialSeries[1];
            const {
                name: renewalName,
                color: renewalColor,
                valueStringified: renewalStringified,
            } = this.setCustomTooltipData(precision, renewalSerie, dataPointIndex);

            const reinforcementSerie = w.globals.initialSeries[2];
            const {
                name: reinforcementName,
                color: reinforcementColor,
                valueStringified: reinforcementStringified,
            } = this.setCustomTooltipData(precision, reinforcementSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: extensionColor,
                    name: extensionName,
                    value: extensionStringified,
                },
                {
                    color: renewalColor,
                    name: renewalName,
                    value: renewalStringified,
                },
                {
                    color: reinforcementColor,
                    name: reinforcementName,
                    value: reinforcementStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setAmountsNetworkPerfCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.amountsNetworkPerfChart.precision;

            const reinforcementLvSerie = w.globals.initialSeries[0];
            const {
                name: reinforcementLvName,
                color: reinforcementLvColor,
                valueStringified: reinforcementLvStringified,
            } = this.setCustomTooltipData(precision, reinforcementLvSerie, dataPointIndex);

            const reinforcementMvSerie = w.globals.initialSeries[1];
            const {
                name: reinforcementMvName,
                color: reinforcementMvColor,
                valueStringified: reinforcementMvStringified,
            } = this.setCustomTooltipData(precision, reinforcementMvSerie, dataPointIndex);

            const actionsPacSerie = w.globals.initialSeries[2];
            const {
                name: actionsPacName,
                color: actionsPacColor,
                valueStringified: actionsPacStringified,
            } = this.setCustomTooltipData(precision, actionsPacSerie, dataPointIndex);

            const actionsExcluPdvRpSerie = w.globals.initialSeries[3];
            const {
                name: actionsExcluPdvRpName,
                color: actionsExcluPdvRpColor,
                valueStringified: actionsExcluPdvRpStringified,
            } = this.setCustomTooltipData(precision, actionsExcluPdvRpSerie, dataPointIndex);

            const actionsPdvRpSerie = w.globals.initialSeries[4];
            const {
                name: actionsPdvRpName,
                color: actionsPdvRpColor,
                valueStringified: actionsPdvRpStringified,
            } = this.setCustomTooltipData(precision, actionsPdvRpSerie, dataPointIndex);

            const operatingResourcesSerie = w.globals.initialSeries[5];
            const {
                name: operatingResourcesName,
                color: operatingResourcesColor,
                valueStringified: operatingResourcesStringified,
            } = this.setCustomTooltipData(precision, operatingResourcesSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[6];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: reinforcementLvColor,
                    name: reinforcementLvName,
                    value: reinforcementLvStringified,
                },
                {
                    color: reinforcementMvColor,
                    name: reinforcementMvName,
                    value: reinforcementMvStringified,
                },
                {
                    color: actionsPacColor,
                    name: actionsPacName,
                    value: actionsPacStringified,
                },
                {
                    color: actionsExcluPdvRpColor,
                    name: actionsExcluPdvRpName,
                    value: actionsExcluPdvRpStringified,
                },
                {
                    color: actionsPdvRpColor,
                    name: actionsPdvRpName,
                    value: actionsPdvRpStringified,
                },
                {
                    color: operatingResourcesColor,
                    name: operatingResourcesName,
                    value: operatingResourcesStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setAmountsConnectionCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.amountsConnectionChart.precision;

            const consumersLvSerie = w.globals.initialSeries[0];
            const {
                name: consumersLvName,
                color: consumersLvColor,
                valueStringified: consumersLvStringified,
            } = this.setCustomTooltipData(precision, consumersLvSerie, dataPointIndex);

            const consumersMvSerie = w.globals.initialSeries[1];
            const {
                name: consumersMvName,
                color: consumersMvColor,
                valueStringified: consumersMvStringified,
            } = this.setCustomTooltipData(precision, consumersMvSerie, dataPointIndex);

            const producersLvSerie = w.globals.initialSeries[2];
            const {
                name: producersLvName,
                color: producersLvColor,
                valueStringified: producersLvStringified,
            } = this.setCustomTooltipData(precision, producersLvSerie, dataPointIndex);

            const producersMvSerie = w.globals.initialSeries[3];
            const {
                name: producersMvName,
                color: producersMvColor,
                valueStringified: producersMvStringified,
            } = this.setCustomTooltipData(precision, producersMvSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[4];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: consumersLvColor,
                    name: consumersLvName,
                    value: consumersLvStringified,
                },
                {
                    color: consumersMvColor,
                    name: consumersMvName,
                    value: consumersMvStringified,
                },
                {
                    color: producersLvColor,
                    name: producersLvName,
                    value: producersLvStringified,
                },
                {
                    color: producersMvColor,
                    name: producersMvName,
                    value: producersMvStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
